import { forwardRef, ReactNode, useEffect, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box } from '@mui/material';
import { PrevButton, NextButton, usePrevNextButtons } from '../../carouselArrowButton/component';
import CarouselHeader from '../../carouselHeader/component';
import type { EmblaOptionsType } from 'embla-carousel';
import { UseCarouselProps, CarouselSettings, useCarouselStyle } from '../style';
import { useCarouselScrollAnalytics } from '../../../analytics/useCarouselScroll';
import { FALLBACK_APP_VERSION, ProductCardVariant } from '../../../utils/constants';
import { ProductCarouselContainer } from '../style';
import { RecommendationType } from '../../../types/common';
import { config } from '../../../config/env';
import { useStaticData } from '../../../contexts/DataProvider.context';
import { isFlagEnabledForCountry } from '../../../utils/helper';
import { getFeatureMinVersion, isFeatureEnabled } from '../../../utils/common.util';

export interface ProductCarouselProps extends UseCarouselProps {
  slides: ReactNode[];
  options?: EmblaOptionsType;
  bgColor?: string;
  featureName?: string;
  topSection?: ReactNode;
  recommendationType?: string;
  footer?: ReactNode;
  settings?: CarouselSettings;
  className?: string;
  eventActionCallback?: (eventType: 'onNextClick' | 'onPrevClick' | 'onScreen' | 'onDotClick', e?: Event) => void;
  onCarouselScroll?: (scrollDirection?: string) => void;
  scrollAnalyticsDebounce?: number;
  header?: {
    title?: ReactNode;
    children?: ReactNode;
  };
  productSize?: any;
  showSeeAll?: boolean | 0 | undefined;
}

export type CarouselHandle = {
  scrollToIndex: (index: number) => void;
};

const ProductCarousel = forwardRef<CarouselHandle, ProductCarouselProps>((props, ref) => {
  const enableHorizonProductCard = config.ENABLE_HORIZON_PRODUCT_CARD;
  const { countryCode, isNative, appVersion } = useStaticData();

  const {
    slides,
    topSection,
    footer,
    options,
    className,
    eventActionCallback,
    recommendationType,
    onCarouselScroll,
    scrollAnalyticsDebounce = 300,
    productSize,
    showSeeAll,
  } = props;

  const {
    getCarouseContainerStyle,
    getCarouseViewportStyle,
    getCarouseSlideRootStyle,
    getCarouseWrapperStyle,
    getCarouseSlideStyle,
    getResponsiveArrowVisibility,
    getCompactCarouselStyle,
  } = useCarouselStyle(props);

  const isHorizonProductCardEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableHorizonProductCard) && isNative);
  }, [countryCode, enableHorizonProductCard, isNative]);

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, countryCode, isHorizonProductCardEnabled]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...options,
    duration: 60,
  });

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const { handleScrollLeft, handleScrollRight, handleManualScroll } = useCarouselScrollAnalytics({
    onScroll: onCarouselScroll,
    debounceMs: scrollAnalyticsDebounce,
  });

  useEffect(() => {
    if (!emblaApi || !onCarouselScroll) return;

    emblaApi.on('scroll', handleManualScroll);

    return () => {
      emblaApi.off('scroll', handleManualScroll);
    };
  }, [emblaApi, handleManualScroll, onCarouselScroll]);

  const arrowStyles = getResponsiveArrowVisibility();

  const handlePrevClick = (e: any) => {
    onPrevButtonClick();
    if (onCarouselScroll) {
      handleScrollLeft();
    }
    if (eventActionCallback && typeof eventActionCallback === 'function') {
      eventActionCallback('onPrevClick', e);
    }
  };

  const handleNextClick = (e: any) => {
    onNextButtonClick();
    if (onCarouselScroll) {
      handleScrollRight();
    }
    if (eventActionCallback && typeof eventActionCallback === 'function') {
      eventActionCallback('onNextClick', e);
    }
  };

  const isSearchStartTopPicksCarousel = recommendationType === RecommendationType.SEARCH_START_TOP_PICKS;
  const isBasketRecommendationsCarousel = recommendationType === RecommendationType.BASKET_RECOMMENDATION;
  const isBuyAgainCarousel = recommendationType === RecommendationType.BUY_AGAIN;

  return (
    <ProductCarouselContainer
      component="section"
      bgColor={props.bgColor}
      className={className}
      data-testid="carousel-base-container"
    >
      {props?.header && !isBuyAgainCarousel && (
        <CarouselHeader title={props?.header?.title}>{props?.header?.children}</CarouselHeader>
      )}
      <Box {...getCarouseWrapperStyle()}>
        {topSection && <Box>{topSection}</Box>}
        <Box {...getCarouseSlideRootStyle()}>
          <Box sx={arrowStyles}>
            {!prevBtnDisabled && <PrevButton onPress={handlePrevClick} disabled={prevBtnDisabled} />}
          </Box>
          <Box {...getCarouseViewportStyle()} ref={emblaRef}>
            <Box {...getCarouseContainerStyle()}>
              {slides.map((slide, index) => {
                const isLastSlide = index === slides?.length - 1;
                return (
                  <Box
                    {...(productSize === ProductCardVariant.NORMAL
                      ? getCarouseSlideStyle(
                          isLastSlide,
                          showSeeAll && !isSearchStartTopPicksCarousel,
                          true,
                          isSearchStartTopPicksCarousel || isBasketRecommendationsCarousel,
                          isHorizonUiUxEnabled,
                          isNative,
                        )
                      : getCompactCarouselStyle())}
                    key={index}
                  >
                    {slide}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={arrowStyles}>
            {!nextBtnDisabled && <NextButton onPress={handleNextClick} disabled={nextBtnDisabled} />}
          </Box>
        </Box>
      </Box>
      {footer && <Box>{footer}</Box>}
    </ProductCarouselContainer>
  );
});

export default ProductCarousel;
